import React, { useEffect, useState } from "react";
import { MenuItemType } from "@/components/atoms/MenuItem/MenuItem";
import { clsx } from "clsx";
import { ChevronRight } from "lucide-react";
import Button from "../Button";
import Link from "../Link";
import "./menuItem.css";
import useAccess from "@/hooks/useAccess";

type Props = {
  item: MenuItemType;
  className?: string;
  isOpen?: boolean;
  activeItem?: string;
  setOpen?: (open: boolean) => void;
  onClick?: (item: MenuItemType, type: "parent" | "child") => void;
};

const MenuItem = ({
  item,
  className,
  isOpen = true,
  activeItem,
  setOpen = () => {},
  onClick = () => {},
}: Props) => {
  const { getAccess } = useAccess();
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const isActive =
    activeItem === item.key ||
    item.children?.find((child) => child.key === activeItem)?.key;

  const actionClasses = {
    active: isActive,
    opened: subMenuOpen,
  };

  const itemClasses = className
    ? clsx(className, actionClasses)
    : clsx("my-1", {
        ...actionClasses,
        "h-10": !subMenuOpen,
        "h-auto": subMenuOpen,
      });

  const linkClasses = clsx("flex items-center", {
    "text-primary": isActive,
  });

  useEffect(() => {
    const isDeactive = activeItem !== item.key;
    const hasActiveChild = item.children?.some(
      (child) => child.key === activeItem
    );

    if (!isOpen || (isDeactive && !hasActiveChild)) {
      setSubMenuOpen(false);
    }

    if (isOpen && isDeactive && hasActiveChild) {
      setSubMenuOpen(true);
    }
  }, [isOpen, activeItem]);

  const handleClick = () => {
    if (item.children?.length) {
      setOpen(true);
      setSubMenuOpen(!subMenuOpen);
    }
    onClick(item, item.children?.length ? "parent" : "child");
  };

  return (
    <li className={itemClasses}>
      <div
        className={clsx(
          "flex items-center w-full  hover:text-primary",
          isOpen ? "justify-between" : "justify-end"
        )}
        onClick={handleClick}
      >
        <Link className={linkClasses} {...(item.url ? { href: item.url } : {})}>
          {item.icon}
          {isOpen && <span className="pl-4">{item.title}</span>}
        </Link>
        {!!(item.children?.length && isOpen) && (
          <Button
            variant="link"
            size="icon"
            className={clsx({ "text-primary": isActive })}
            onClick={() => setSubMenuOpen(!subMenuOpen)}
          >
            <ChevronRight className="chevron transition-all" />
          </Button>
        )}
      </div>
      {subMenuOpen && (
        <ul className="pl-5 py-2">
          {item.children?.filter(child => !child.permissions || getAccess(child.permissions)).map((child) => (
            <MenuItem
              item={child}
              className="flex items-center h-8 my-1"
              key={child.key}
              activeItem={activeItem}
              onClick={onClick}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

export default MenuItem;
